import { useState } from 'react';
import { ActionError, ShownOnce, useAction } from '../../components/action.tsx';
import { DataTable } from '../../components/data-table.tsx';
import { formatDate } from '../../components/format.ts';
import { useConsole } from '../../data/console-context.tsx';
import type { ApiKeyRow, CreatedKey } from '../../data/rows.ts';

export function AllowedOrigins(props: {
  origins: readonly string[];
  canEdit: boolean;
  onChanged: () => void;
}) {
  const { data, tenant } = useConsole();
  const [text, setText] = useState(props.origins.join('\n'));
  const action = useAction();
  const origins = text
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line !== '');
  const changed = origins.join() !== props.origins.join();

  return (
    <section>
      <h2>Allowed sites</h2>
      <p className="muted">
        The origins that may embed the assistant, one per line, such as{' '}
        <code>https://app.example.com</code>. Links to them open directly from replies.
      </p>
      <form
        className="form"
        onSubmit={(event) => {
          event.preventDefault();
          void action
            .run(() => data.setAllowedOrigins(tenant.id, origins))
            .then((ok) => ok && props.onChanged());
        }}
      >
        <textarea
          aria-label="Allowed origins"
          rows={Math.max(3, origins.length + 1)}
          value={text}
          disabled={!props.canEdit || action.pending}
          onChange={(e) => setText(e.target.value)}
        />
        {props.canEdit && (
          <div className="row">
            <button type="submit" className="primary" disabled={!changed || action.pending}>
              Save sites
            </button>
          </div>
        )}
        <ActionError error={action.error} />
      </form>
    </section>
  );
}

export function ApiKeys(props: {
  keys: readonly ApiKeyRow[];
  canEdit: boolean;
  onChanged: () => void;
}) {
  const { data, tenant } = useConsole();
  const action = useAction();
  const [created, setCreated] = useState<CreatedKey | null>(null);
  const create = (kind: ApiKeyRow['kind']) =>
    void action.run(async () => {
      setCreated(await data.createApiKey(tenant.id, kind));
      props.onChanged();
    });

  return (
    <section>
      <h2>API keys</h2>
      <p className="muted">
        Your backend uses a secret key to mint sessions; the page uses the publishable key. A secret
        key is shown once, when it is created.
      </p>
      {created !== null && (
        <ShownOnce label="New key." value={created.value} onDismiss={() => setCreated(null)} />
      )}
      <DataTable
        caption="Keys"
        columns={[
          {
            header: 'Key',
            cell: (row) => <code>{row.publishableKey ?? `${row.id} (secret)`}</code>,
          },
          { header: 'Kind', cell: (row) => row.kind },
          { header: 'Mode', cell: (row) => row.mode },
          { header: 'Created', cell: (row) => formatDate(row.createdAt) },
          {
            header: 'Status',
            cell: (row) =>
              row.revokedAt === null ? (
                <span className="chip chip-success">Active</span>
              ) : (
                <span className="chip">Revoked {formatDate(row.revokedAt)}</span>
              ),
          },
          ...(props.canEdit
            ? [
                {
                  header: 'Actions',
                  cell: (row: ApiKeyRow) =>
                    row.revokedAt === null && (
                      <button
                        type="button"
                        className="danger"
                        disabled={action.pending}
                        onClick={() =>
                          void action
                            .run(() => data.revokeApiKey(tenant.id, row.id))
                            .then((ok) => ok && props.onChanged())
                        }
                      >
                        Revoke
                      </button>
                    ),
                },
              ]
            : []),
        ]}
        rows={props.keys}
      />
      {props.canEdit && (
        <div className="row">
          <button type="button" disabled={action.pending} onClick={() => create('secret')}>
            New secret key
          </button>
          <button type="button" disabled={action.pending} onClick={() => create('publishable')}>
            New publishable key
          </button>
        </div>
      )}
      <ActionError error={action.error} />
    </section>
  );
}
